/**
 * @module BreadcrumbsRoot
 *
 * @see https://0.vuetifyjs.com/components/semantic/breadcrumbs
 *
 * @remarks
 * Root component for responsive breadcrumb navigation using declarative children.
 *
 * Uses createBreadcrumbs composable as its backing model (like TabsRoot uses createStep).
 * Also creates a Group context with enrollment for overflow tracking, and Overflow
 * context to determine which items fit. Visibility is controlled via selection state.
 */

<script lang="ts">
  // Components
  import { Atom } from '#v0/components/Atom'

  // Composables
  import { createBreadcrumbs } from '#v0/composables/createBreadcrumbs'
  import { createContext } from '#v0/composables/createContext'
  import { createGroup } from '#v0/composables/createGroup'
  import { createOverflow } from '#v0/composables/createOverflow'
  import { useLocale } from '#v0/composables/useLocale'

  // Constants
  import { IN_BROWSER } from '#v0/constants/globals'

  // Utilities
  import { isNull, pxToNumber } from '#v0/utilities'
  import { shallowRef, toRef, useTemplateRef, watch } from 'vue'

  // Types
  import type { AtomProps } from '#v0/components/Atom'
  import type { ID } from '#v0/types'
  import type { BreadcrumbsRootContext, BreadcrumbsTicket } from './types'
  import type { ShallowRef } from 'vue'

  export type { BreadcrumbsRootContext, BreadcrumbsTicket, BreadcrumbsTicketType } from './types'

  export interface BreadcrumbsRootProps extends AtomProps {
    /** Namespace for dependency injection */
    namespace?: string
    /** Default divider character/text */
    divider?: string
    /** Default ellipsis character/text */
    ellipsis?: string
    /** Gap between items in pixels. @default 8 */
    gap?: number
    /** Accessible label for the navigation landmark */
    label?: string
  }

  export interface BreadcrumbsRootSlotProps {
    /** Whether items are being truncated */
    isOverflowing: boolean
    /** Number of items that fit in available space */
    capacity: number
    /** Total number of registered items */
    total: number
    /** Current depth of the breadcrumb path */
    depth: number
    /** Whether at root level */
    isRoot: boolean
    /** Navigate to root */
    first: () => void
    /** Navigate up one level */
    prev: () => void
    /** Navigate to a specific item */
    select: (id: ID) => void
    /** Attributes to bind to the root element */
    attrs: {
      'aria-label': string
      'role': 'navigation' | undefined
    }
  }

  export const [useBreadcrumbsRoot, provideBreadcrumbsRoot] = /* @__PURE__ */ createContext<BreadcrumbsRootContext>()
</script>

<script setup lang="ts">
  defineOptions({ name: 'BreadcrumbsRoot' })

  defineSlots<{
    default: (props: BreadcrumbsRootSlotProps) => unknown
  }>()

  const {
    namespace = 'v0:breadcrumbs',
    as = 'nav',
    renderless,
    divider = '/',
    ellipsis = '…',
    gap = 8,
    label,
  } = defineProps<BreadcrumbsRootProps>()

  const locale = useLocale()
  const containerRef = useTemplateRef('container')

  const breadcrumbs = createBreadcrumbs({ enroll: true })

  const group = createGroup<BreadcrumbsTicket>({
    enroll: true,
    multiple: true,
    // Membership must be a tracked dependency: children register after the
    // Root renders, and both the hidden count and the ellipsis's disclosure
    // state are read from this registry. Never passed to a template v-for.
    reactive: true,
  })

  // Reserved widths — first pair + ellipsis are excluded from the overflow
  // measurement pool and tracked separately so capacity is accurate.
  const ellipsisWidth = shallowRef(0)
  const firstItemWidth = shallowRef(0)
  const firstDividerWidth = shallowRef(0)

  const overflow = createOverflow({
    container: () => containerRef.value?.element as Element | undefined,
    gap: () => gap,
    reserved: () => {
      let r = 0
      if (firstItemWidth.value > 0) r += firstItemWidth.value + gap
      if (firstDividerWidth.value > 0) r += firstDividerWidth.value + gap
      if (ellipsisWidth.value > 0) r += ellipsisWidth.value + gap
      return r
    },
    reverse: true,
  })

  const isOverflowing = overflow.isOverflowing

  // Derived here rather than in each ellipsis — the Root owns the registry and
  // is already where isOverflowing and hiddenCount are worked out.
  const hasActivator = toRef(() => group.values().some(t => t.type === 'activator'))

  // Disclosure state — when true, collapsed items are revealed (toggled by
  // an interactive BreadcrumbsEllipsis).
  const expanded = shallowRef(false)

  // Written imperatively by the visibility watcher below rather than derived.
  // The group is not `reactive`, so membership is not a tracked dependency and
  // a getter would go stale inside render effects when crumbs come and go.
  const hiddenCount = shallowRef(0)

  // Element measurement routing — first item/divider write to reserved refs,
  // everything else feeds into the overflow pool.
  let _firstItemIndex: number | null = null
  let _firstDividerIndex: number | null = null

  function measureToRef (el: Element | undefined, target: ShallowRef<number>) {
    if (!IN_BROWSER || !el) {
      target.value = 0
      return
    }
    const htmlEl = el as HTMLElement
    const style = getComputedStyle(htmlEl)
    const marginX = pxToNumber(style.marginLeft) + pxToNumber(style.marginRight)
    target.value = (htmlEl.offsetWidth || 0) + marginX
  }

  function measureElement (index: number, type: 'item' | 'divider', el: Element | undefined) {
    if (type === 'item' && (isNull(_firstItemIndex) || index <= _firstItemIndex)) {
      _firstItemIndex = el ? index : null
      measureToRef(el, firstItemWidth)
      return
    }
    if (type === 'divider' && (isNull(_firstDividerIndex) || index <= _firstDividerIndex)) {
      _firstDividerIndex = el ? index : null
      measureToRef(el, firstDividerWidth)
      return
    }
    overflow.measure(index, el)
  }

  watch(
    [() => overflow.capacity.value, () => group.size, expanded],
    ([capacity]) => {
      const all = group.values()

      // Separate ellipsis tickets from content (items + dividers)
      type Ticket = typeof all[number]
      const contentTickets: Ticket[] = []
      const ellipsisTickets: Ticket[] = []

      for (const ticket of all) {
        // Activators live inside an ellipsis and occupy no space of their own,
        // so they take no part in the layout maths.
        if (ticket.type === 'activator') continue
        if (ticket.type === 'ellipsis') {
          ellipsisTickets.push(ticket)
        } else {
          contentTickets.push(ticket)
        }
      }

      const contentSize = contentTickets.length
      const measuredCount = Math.max(0, contentSize - 2)

      const fI = firstItemWidth.value
      const fD = firstDividerWidth.value
      const eW = ellipsisWidth.value
      const reserved = fI + gap + fD + gap + eW + gap

      if (
        ellipsisTickets.length === 0 ||
        capacity === Infinity ||
        capacity >= measuredCount
      ) {
        // Everything fits, or no overflow UI composed — show all content, hide
        // ellipsis. Truncation is gone, so a disclosure left open from a
        // narrower viewport is stale.
        hiddenCount.value = 0
        if (expanded.value) expanded.value = false
        for (const t of ellipsisTickets) group.unselect(t.id)
        for (const t of contentTickets) {
          if (!t.isSelected.value) group.select(t.id)
        }
        return
      }

      const lastIndex = contentSize - 1
      const poolStart = 2
      const poolSize = Math.max(0, lastIndex - poolStart + 1)
      const toShow = Math.min(poolSize, capacity)
      const showStart = lastIndex - toShow + 1

      // Plan the truncated layout as data before touching selection. The
      // disclosure only changes which plan gets applied, so the hidden count
      // stays a function of the current measurements either way.
      const visible = Array.from({ length: contentSize }, (_, i) => i < 2 || i >= showStart)
      let showEllipsis = true

      // showStart > poolStart puts the separator inside the pool, so it needs no
      // lower-bound check of its own.
      if (toShow > 0 && showStart > poolStart && contentTickets[showStart]!.type === 'item') {
        const sep = showStart - 1
        if (contentTickets[sep]!.type === 'divider') visible[sep] = true
      }

      if (capacity === 0) {
        // Getting past the early return means capacity < measuredCount, so there
        // are at least three content tickets and every index below is in range.
        visible[lastIndex] = true

        const w = overflow.width.value

        if (w < reserved + fD) visible[1] = false
        if (w < fI + gap + eW + gap) showEllipsis = false
        if (w < fI + gap) visible[0] = false
      }

      let hidden = 0
      for (let i = 0; i < contentSize; i++) {
        if (!visible[i] && contentTickets[i]!.type === 'item') hidden++
      }
      hiddenCount.value = hidden

      // An open disclosure keeps the ellipsis so it can collapse the trail again.
      for (const t of ellipsisTickets) {
        if (showEllipsis || expanded.value) group.select(t.id)
        else group.unselect(t.id)
      }

      for (let i = 0; i < contentSize; i++) {
        const t = contentTickets[i]!
        if (expanded.value || visible[i]) {
          if (!t.isSelected.value) group.select(t.id)
        } else {
          group.unselect(t.id)
        }
      }
    },
    { immediate: true },
  )

  provideBreadcrumbsRoot(namespace, {
    breadcrumbs,
    group,
    overflow,
    divider: toRef(() => divider),
    ellipsis: toRef(() => ellipsis),
    isOverflowing,
    expanded,
    hasActivator,
    hiddenCount,
    ellipsisWidth,
    measureElement,
  })

  const slotProps = toRef((): BreadcrumbsRootSlotProps => ({
    isOverflowing: overflow.isOverflowing.value,
    capacity: overflow.capacity.value,
    total: group.size,
    depth: breadcrumbs.depth.value,
    isRoot: breadcrumbs.isRoot.value,
    first: breadcrumbs.first,
    prev: breadcrumbs.prev,
    select: breadcrumbs.select,
    attrs: {
      'aria-label': label ?? locale.ti('Breadcrumbs.label') ?? 'Breadcrumbs',
      'role': as === 'nav' ? undefined : 'navigation',
    },
  }))
</script>

<template>
  <Atom
    ref="container"
    :as
    :renderless
    v-bind="slotProps.attrs"
  >
    <slot v-bind="slotProps" />
  </Atom>
</template>
